import { getMessage } from '@tcc/i18n';
import { DEFAULT_SEASON_YEAR } from '../constants/seasonIds';

export const DEFAULT_SEASONS = {
  [DEFAULT_SEASON_YEAR]: {
    name: DEFAULT_SEASON_YEAR.toString(),
    year: DEFAULT_SEASON_YEAR,
  },
};

/**
 * Sort the seasons so that the newest season (in terms of year) is first. If more than one
 * season have the same year, sort alphabetically by crop name.
 * @param {Object} seasons
 * @return {Array} - Returns the seasons sorted and with correct keys
 */
export function sortSeasonKeys(seasons) {
  const seasonKeys = Object.keys(seasons);

  return seasonKeys.sort((key1, key2) => {
    const season1 = seasons[key1];
    const season2 = seasons[key2];
    const hasGreaterYear = season1.year > season2.year;
    const hasAlphabeticalName =
      season1.year === season2.year && season1.name < season2.name;

    return hasGreaterYear || hasAlphabeticalName ? -1 : 1;
  });
}

/**
 * Create a season object, pulling info from its season tag and determining its name
 * @param {Object} currSeason
 * @return {Object}
 */
function extractSeasonData(currSeason, cropMap, { cropSeasonFilterEnabled } = { cropSeasonFilterEnabled: false }) {
  const { season } = currSeason;
  const [year, cropFromSeasonTag, region, position, seasonType] = season.split(':');
  const yearNum = parseInt(year, 10);

  const cropExists = cropFromSeasonTag !== undefined;
  const isValidCrop = cropExists && cropMap[cropFromSeasonTag] !== undefined;

  /*
   * From Sprout migration and on, we'll only show the name of the crops that exist in the master list
   * If a given "official supported" crop is missing it's translation, show it's name in english just as how is coming from the backend
   * Unsupported crops AKA Crops not in the Crop Map provided by Sprout are going to default to `Other`
   */
  /*
   * Changes requested in LI-2160: show "Unknown" instead of "Other" for unknown or not valid crops
   * When FF cropSeasonFilterEnabled is true, we show "Unknown" for crops that are not in the cropMap.
   */

  const isOther = cropFromSeasonTag === 'other';
  // If the crop is not valid, we default to "Unknown" or "Other" based on the flag
  const isOtherKey = cropSeasonFilterEnabled ? 'unknown' : 'other';

  const cropNameTranslated = getMessage(`crop.${isOther ? isOtherKey : cropFromSeasonTag}`);
  const cropName = isValidCrop
    ? cropNameTranslated ?? cropMap[cropFromSeasonTag].name
    : getMessage(`crop.${isOtherKey}`);

  const seasonName = cropExists ? `${year} ${cropName}` : year;

  return {
    key: currSeason.season,
    seasonData: {
      ...currSeason,
      name: seasonName,
      year: yearNum,
      crop: cropFromSeasonTag,
      region,
      position,
      seasonType,
    },
  };
}

/**
 * Combine each individual season object in the seasons array into one combined object.
 * @param {Array} seasonsArr
 * @return {Object} - Returns the combined seasons
 */
export function combineSeasons(seasonsArr, cropMap, options) {
  // Merge all season lists together; retain unique key-values for each inner season object
  const combinedSeasons = seasonsArr.reduce((seasons, season) => {
    if (!season.season) {
      return seasons;
    }

    const { key, seasonData } = extractSeasonData(season, cropMap, options);
    // Don't count years that have been repeated and only keep the ones that have
    // crop data.
    const index = Object.values(seasons).findIndex((prevSeason) => seasonData.year === prevSeason.year && !seasonData.crop);

    return index === -1
      ? {
          ...seasons,
          [key]: seasons[key]
            ? {
                ...seasons[key],
                ...seasonData,
              }
            : seasonData,
        }
      : seasons;
  }, {});

  // If no seasons were given, return a default season
  return Object.keys(combinedSeasons).length
    ? combinedSeasons
    : DEFAULT_SEASONS;
}

/**
 * Attach the liquid seasons data to each season that belongs to the same year.
 * @param {Object} seasons
 * @param {Array} [liquidSeasons=[]]
 * @return {Object} - Returns the modified seasons
 */
export function addLiquidSeasonData(seasons, liquidSeasons = []) {
  if (!liquidSeasons.length) {
    return seasons;
  }

  // Create new season objects for liquid data with a unique year
  const newSeasons = liquidSeasons
    .filter(
      ({ year }) =>
        !Object.keys(seasons).some((key) => seasons[key].year === year)
    )
    .reduce((_seasons, application) => {
      const yearStr = application.year.toString();
      return {
        ..._seasons,
        [application.year]: {
          liquidData: [application],
          name: yearStr,
          season: yearStr,
          year: application.year,
        },
      };
    }, seasons);

  // Add liquid data to season objects that share the same year
  return Object.keys(newSeasons).reduce((_seasons, key) => {
    const season = _seasons[key];
    const liquidData = liquidSeasons.filter(({ year }) => year === season.year);
    return {
      ..._seasons,
      [key]: liquidData.length ? { ...season, liquidData } : season,
    };
  }, newSeasons);
}

/**
 * Add the season type to the name of any season that has the same crop and year as another season
 * @param {Object} seasons
 * @param {Array} seasonKeys
 * @return {Object} - Returns the modified seasons
 */
export function addSeasonTypesToDuplicateNames(seasons, seasonKeys) {
  // Find all seasons whose name is the same as another season's
  const duplicateSeasons = seasonKeys.filter((seasonKey) =>
    seasonKeys.some(
      (seasonKey2) =>
        seasonKey !== seasonKey2 &&
        seasons[seasonKey].name === seasons[seasonKey2].name
    )
  );

  return seasonKeys.reduce((_seasons, seasonKey) => {
    const { name, seasonType } = seasons[seasonKey];
    return {
      ..._seasons,
      [seasonKey]: {
        ...seasons[seasonKey],
        name: duplicateSeasons.includes(seasonKey)
          ? `${name} ${getMessage(`season.${seasonType}`)}`
          : name,
      },
    };
  }, seasons);
}

export function extractSeasons(seasonsArr, liquidSeasons, cropMap, options = { cropSeasonFilterEnabled: false }) {
  const combinedSeasons = combineSeasons(seasonsArr, cropMap, options);
  const seasonsWithApplications = addLiquidSeasonData(
    combinedSeasons,
    liquidSeasons
  );
  const sortedSeasonKeys = sortSeasonKeys(seasonsWithApplications);
  const seasons = addSeasonTypesToDuplicateNames(
    seasonsWithApplications,
    sortedSeasonKeys
  );

  return { seasons, sortedSeasonKeys };
}

export function extractMosaicSeasons(data) {
  const layers = Object.keys(data);
  return layers.reduce((seasons, layer) => {
    const dates = Object.keys(data[layer]);
    const layerYears = dates
      .reduce((years, date) => [...years, date.split('-')[0]], [])
      .filter((year, pos, years) => years.indexOf(year) === pos)
      .map((year) => ({ season: year }));
    return [...seasons, ...layerYears];
  }, []);
}
